import {
  APP_INITIALIZER,
  FactoryProvider,
  Inject,
  Injectable,
  InjectionToken,
  LOCALE_ID,
  NgModule,
  Provider
} from '@angular/core';

import {
  I18NEXT_INSTANCE,
  I18NEXT_SERVICE,
  I18NextLoadResult,
  I18NextModule,
  ITranslationService
} from 'angular-i18next';
import { BackendModule, PostProcessorModule } from 'i18next';
import * as i18next from 'i18next';
import I18NextFetchBackend from 'i18next-fetch-backend';

import { BreadcrumbItem } from '@hdms/shared/common-types';
import {
  BREADCRUMB_TRANSLATIONS,
  BreadcrumbTranslations,
  CART_MESSAGES_TRANSLATIONS,
  CartMessagesTranslations,
  DATEPICKER_TRANSLATION,
  DOCUMENT_UPLOAD_TRANSLATIONS,
  DatepickerTranslation,
  DocumentUploadTranslations,
  INDICATOR_LABEL_TRANSLATIONS,
  IndicatorLabelTranslations,
  MASTERCARD_TRANSLATIONS,
  MastercardTranslations,
  OPTION_CARDS_TRANSLATIONS,
  OptionCardsTranslations,
  RIBBON_TRANSLATIONS,
  RibbonTranslations,
  SMARTBAR_TRANSLATIONS,
  SmartbarTranslations
} from '@hdms/shared/ui-core';
import {
  COMMON_PAGE_TITLE,
  CONSENT_MANAGEMENT_TRANSLATIONS,
  ConsentManagementTranslations,
  EnvironmentService,
  I18N_NAMESPACE,
  PlatformService,
  ROUTER_BREADCRUMB_TRANSLATE,
  RouterBreadcrumbTranslate,
  StorageService,
  StorageType,
  TRANSLATE_FN,
  TranslateFn,
  VALIDATION_MESSAGES,
  ValidationMessages,
  ValidatorProps,
  WindowRefService,
  hasOwnProp,
  kebapToCamelCase
} from '@hdms/shared/util-core';

export const I18NEXT_BACKEND = new InjectionToken('apps.website.i18nextBackend', {
  factory: () => I18NextFetchBackend
});

@Injectable()
export class ValidationTranslations implements ValidationMessages {
  constructor(@Inject(I18NEXT_SERVICE) private readonly i18NextService: ITranslationService) {}

  get(key: string, context: string, props: ValidatorProps, ns: string = null): string {
    const options = { context, defaultValue: null, ns, ...props };
    return this.i18NextService.t(`validation.${key}`, options);
  }
}

@Injectable()
export class DateTranslationService implements DatepickerTranslation {
  weekdayShort = [];
  monthShort = [];
  monthFull = [];
  ariaCalendar = this.i18NextService.t('common.label.button.openCalendar');
  ariaClear = this.i18NextService.t('common.label.button.clear');

  constructor(@Inject(I18NEXT_SERVICE) private readonly i18NextService: ITranslationService) {
    this.weekdayShort = Object.values(i18NextService.t('date.weekdayShort', { returnObjects: true }));
    this.monthFull = Object.values(i18NextService.t('date.monthFull', { returnObjects: true }));
    this.monthShort = Object.values(i18NextService.t('date.monthShort', { returnObjects: true }));
  }
}

@Injectable()
export class RouterBreadcrumbTranslationService implements RouterBreadcrumbTranslate {
  i18nNamespaceProp = 'i18nextNamespaces';
  rootBreadCrumbItem: BreadcrumbItem = {
    title: 'home',
    href: '/'
  };

  constructor(@Inject(I18NEXT_SERVICE) private readonly i18NextService: ITranslationService) {}

  public translate(breadcrumbItems: BreadcrumbItem[], namespaces: string[]): BreadcrumbItem[] {
    return breadcrumbItems.map(bc => {
      const keyPrefix = bc.title.includes('.') ? '' : 'breadcrumb.';
      const key = `${keyPrefix}${bc.title}`;
      const ns = namespaces.find(n => this.i18NextService.exists(key, { ns: n }));
      return {
        ...bc,
        title: this.i18NextService.t(key, { ...bc.routeParams, ns })
      };
    });
  }
}

/**
 * This adapter service re-routes any localization calls to Angular `$localize` method to I18NextService.
 * It's required for resolving the @angular/localize dependency of NgBootstrap, but will also work for
 * anything else depending on @angular/localize package.
 */
@Injectable({ providedIn: 'root' })
export class NgLocalizeAdapterService {
  constructor(@Inject(I18NEXT_SERVICE) i18NextService: ITranslationService, { nativeWindow }: WindowRefService) {
    nativeWindow.$localize = ([key]: string[]) => {
      const sanitizedKey = kebapToCamelCase(key?.replace(/(:@@)([^:]*)(.*)/, '$2'));
      return i18NextService.t(sanitizedKey);
    };
  }
}

export function localeIdFactory(envService: EnvironmentService): string {
  return envService.languageTag;
}

/**
 * i18next post-processor returning correctly namespaced translation keys for debugging purpose.
 * Workaround as i18next even in ci-mode is not capable to output translation key
 * and its related namespace without falling back to default namespace.
 * Only activated when adding query parameter "?showMessageCode=true".
 */
const debugKeysOutputProcessor: PostProcessorModule = {
  type: 'postProcessor',
  name: 'debugKeysOutput',
  process: (value, key, options, translator) => {
    const tOptions = translator.options;
    const translationKey = key[key.length - 1];

    const keys = translationKey.split(tOptions.keySeparator);

    let validNameSpace = '';
    const allNsData = translator.resourceStore.data[tOptions.fallbackLng || tOptions.lng];

    for (const nsKey in allNsData) {
      if (hasOwnProp(allNsData, nsKey)) {
        const val = keys.reduce((acc, curr) => acc && curr && acc[curr], allNsData[nsKey]);
        if (val) {
          validNameSpace = nsKey;
          break;
        }
      }
    }

    return `${validNameSpace}${tOptions.nsSeparator}${translationKey}`;
  }
};

const I18N_DEFAULT_NS = 'website';

export function i18nextInit(
  i18nextService: ITranslationService,
  i18nextBackend: BackendModule,
  { languageTagLong: appLang, translationPath, pathToAssets, releaseVersion, isProduction }: EnvironmentService,
  winRef: WindowRefService,
  platform: PlatformService,
  storageService: StorageService
): () => Promise<I18NextLoadResult> {
  // initial namespace used also as default and fallback
  const defaultNS = I18N_DEFAULT_NS;

  // For correct pluralization i18next expects the language to be in long languageTag (`fr-FR`) format:
  // https://www.i18next.com/how-to/faq#how-should-the-language-codes-be-formatted

  //for conveniently displaying new translation keys while developing, add 'en' as translation fallback
  const fallbackLng = isProduction ? null : 'en';
  const supportedLngs = isProduction ? [appLang] : [appLang, fallbackLng];

  const i18nPath = translationPath || `${pathToAssets}/assets/i18n`;
  // add releaseVersion as cache-buster param in case translations are retrieved from static S3 bucket path
  const cacheBuster = platform.isBrowser && translationPath ? `?${releaseVersion}` : '';

  let showMessageCode = false;
  if (platform.isBrowser) {
    const showMessageCodeParam = 'showMessageCode';
    const searchParams = new URL(winRef.nativeWindow.location.href).searchParams;
    if (searchParams.has(showMessageCodeParam)) {
      searchParams.get(showMessageCodeParam) === 'true'
        ? storageService.setItem(showMessageCodeParam, true, StorageType.SESSION)
        : storageService.removeItem(showMessageCodeParam, StorageType.SESSION);
    }
    showMessageCode = storageService.getItem(showMessageCodeParam, StorageType.SESSION);
  }

  return () =>
    i18nextService
      .use(debugKeysOutputProcessor)
      .use(i18nextBackend)
      .init({
        supportedLngs,
        lng: appLang,
        fallbackLng,
        returnEmptyString: false,
        load: 'currentOnly',
        // https://www.i18next.com/misc/json-format#i18next-json-v3
        compatibilityJSON: 'v3',
        interpolation: {
          // since i18next v.21 `skipOnVariables` defaults to true, preventing translation nesting.
          // disable it globally to properly parse existing usages of `$t(some.translation.string)`
          skipOnVariables: false
        },
        keySeparator: '.',
        nsSeparator: ':',
        postProcess: showMessageCode && ['debugKeysOutput'],
        appendNamespaceToMissingKey: true,
        initImmediate: platform.isBrowser,
        backend: {
          // As translation filenames are suffixed with locale format using underscore (`en_US`),
          // dash in internal languageTagLong format needs to be replaced with underscore.
          loadPath: (lng: string[], ns: string[]) =>
            `${i18nPath}/${ns.toString()}.${lng.toString().replace('-', '_')}.json${cacheBuster}`
        },
        ns: [defaultNS],
        defaultNS,
        fallbackNS: defaultNS
      });
}

export function translateFactory(i18NextService: ITranslationService): TranslateFn {
  return i18NextService.t.bind(i18NextService);
}

const pageTitleTranslationFactory = (i18NextService: ITranslationService): string =>
  i18NextService.t('common.label.pageTitle');

const breadcrumbTranslationFactory = (i18NextService: ITranslationService): BreadcrumbTranslations => ({
  ariaLabel: i18NextService.t('breadcrumb.ariaLabel')
});

const consentManagementTranslationFactory = (i18NextService: ITranslationService): ConsentManagementTranslations => ({
  privacyPolicyUrl: i18NextService.t('common.urls.didomiPrivacyPolicy')
});

const smartbarTranslationFactory = (i18NextService: ITranslationService): SmartbarTranslations => ({
  ariaLabel: i18NextService.t('smartbar.ariaLabel')
});

const ribbonTranslationFactory = (i18NextService: ITranslationService): RibbonTranslations => ({
  ariaLabel: i18NextService.t('ribbon.ariaLabel')
});

const indicatorLabelTranslationFactory = (i18NextService: ITranslationService): IndicatorLabelTranslations => ({
  ultimate: i18NextService.t('common.label.performance.ultimate'),
  premium: i18NextService.t('common.label.performance.premium'),
  standard: i18NextService.t('common.label.performance.standard')
});

const documentUploadTranslationFactory = (i18NextService: ITranslationService): DocumentUploadTranslations => ({
  deleteButtonLabel: i18NextService.t('common.label.documentUpload.delete'),
  dictFileTooBig: i18NextService.t('common.label.documentUpload.messages.fileTooBig', {
    interpolation: { skipOnVariables: true }
  }),
  dictInvalidFileType: i18NextService.t('common.label.documentUpload.messages.invalidFileType'),
  noFileSelectedLabel: i18NextService.t('common.label.documentUpload.noFile'),
  uploadButtonLabel: i18NextService.t('common.label.documentUpload.upload'),
  uploadSystemErrorMessage: i18NextService.t('common.label.documentUpload.messages.uploadSystemError'),
  dragAndDropLabel: i18NextService.t('common.label.documentUpload.dragAndDrop.label'),
  dragAndDropHint: i18NextService.t('common.label.documentUpload.dragAndDrop.hint')
});

const mastercardTranslationFactory = (i18NextService: ITranslationService): MastercardTranslations => ({
  noProductDetails: i18NextService.t('product.labels.noProductDetails'),
  noTechnicalDetails: i18NextService.t('product.labels.noTechnicalDetails'),
  compare: i18NextService.t('product.labels.compare'),
  fromPricePrefix: i18NextService.t('product.labels.fromPricePrefix'),
  errorHeadline: i18NextService.t('product.messages.priceError.headline'),
  contactUs: i18NextService.t('product.messages.priceError.contactUs'),
  ariaCompareAdd: i18NextService.t('product.labels.compareAdd', {
    interpolation: { skipOnVariables: true }
  }),
  ariaCompareRemove: i18NextService.t('product.labels.compareRemove', {
    interpolation: { skipOnVariables: true }
  }),
  quantity: i18NextService.t('product.labels.quantity'),
  totalPieces: i18NextService.t('product.labels.totalPieces'),
  selectText: i18NextService.t('product.labels.selectText'),
  selectedText: i18NextService.t('product.labels.selectedText')
});

const optionCardsTranslationFactory = (i18NextService: ITranslationService): OptionCardsTranslations => ({
  currentOptionDescriptionHidden: i18NextService.t('common.label.form.currentOptionDescriptionHidden'),
  showMoreOptions: i18NextService.t('common.label.form.showMoreOptions')
});

export const cartMessagesTranslationFactory = (i18NextService: ITranslationService): CartMessagesTranslations => ({
  addToCartSuccessTitle: i18NextService.t('cart.addToCart.message.success.title'),
  goToCartLabel: i18NextService.t('common.label.button.goToCart'),
  addToCartSuccessMessage: (count: number, name?: string) =>
    i18NextService.t('cart.addToCart.message.success.copy', { count, name, context: name ? 'withName' : null }),
  roundUpMessage: {
    headline: i18NextService.t('cart.addToCart.message.roundUp.title'),
    copy: (count: number) =>
      i18NextService.t('cart.addToCart.message.roundUp.copy', { count, context: count ? null : 'generic' })
  }
});

export const I18N_PROVIDERS: Provider[] = [
  {
    provide: APP_INITIALIZER,
    useFactory: i18nextInit,
    deps: [I18NEXT_SERVICE, I18NEXT_BACKEND, EnvironmentService, WindowRefService, PlatformService, StorageService],
    multi: true
  },
  {
    provide: I18N_NAMESPACE,
    useValue: I18N_DEFAULT_NS
  },
  {
    provide: LOCALE_ID,
    deps: [EnvironmentService],
    useFactory: localeIdFactory
  },
  {
    provide: TRANSLATE_FN,
    deps: [I18NEXT_SERVICE],
    useFactory: translateFactory
  },
  {
    provide: VALIDATION_MESSAGES,
    useClass: ValidationTranslations
  },
  {
    provide: DATEPICKER_TRANSLATION,
    useClass: DateTranslationService
  },
  {
    provide: ROUTER_BREADCRUMB_TRANSLATE,
    useClass: RouterBreadcrumbTranslationService
  },
  {
    provide: BREADCRUMB_TRANSLATIONS,
    useFactory: breadcrumbTranslationFactory,
    deps: [I18NEXT_SERVICE]
  },
  {
    provide: CONSENT_MANAGEMENT_TRANSLATIONS,
    useFactory: consentManagementTranslationFactory,
    deps: [I18NEXT_SERVICE]
  },
  {
    provide: SMARTBAR_TRANSLATIONS,
    useFactory: smartbarTranslationFactory,
    deps: [I18NEXT_SERVICE]
  },
  {
    provide: RIBBON_TRANSLATIONS,
    useFactory: ribbonTranslationFactory,
    deps: [I18NEXT_SERVICE]
  },
  {
    provide: INDICATOR_LABEL_TRANSLATIONS,
    useFactory: indicatorLabelTranslationFactory,
    deps: [I18NEXT_SERVICE]
  },
  {
    provide: DOCUMENT_UPLOAD_TRANSLATIONS,
    useFactory: documentUploadTranslationFactory,
    deps: [I18NEXT_SERVICE]
  },
  {
    provide: MASTERCARD_TRANSLATIONS,
    useFactory: mastercardTranslationFactory,
    deps: [I18NEXT_SERVICE]
  },
  {
    provide: OPTION_CARDS_TRANSLATIONS,
    useFactory: optionCardsTranslationFactory,
    deps: [I18NEXT_SERVICE]
  },
  {
    provide: CART_MESSAGES_TRANSLATIONS,
    useFactory: cartMessagesTranslationFactory,
    deps: [I18NEXT_SERVICE]
  },
  {
    provide: COMMON_PAGE_TITLE,
    useFactory: pageTitleTranslationFactory,
    deps: [I18NEXT_SERVICE]
  }
];

/**
 * In SSR scenario always provide a new i18next instance, as otherwise same
 * global static i18next instance is being used, which can lead to race conditions
 * with unexpected language output for concurrent requests using different languages.
 */
export const I18NEXT_INSTANCE_PROVIDER: FactoryProvider = {
  provide: I18NEXT_INSTANCE,
  useFactory: () => i18next.createInstance()
};

@NgModule({
  imports: [I18NextModule.forRoot()],
  providers: [I18N_PROVIDERS],
  exports: [I18NextModule]
})
export class AppTranslationModule {
  constructor(private readonly localizeAdapter: NgLocalizeAdapterService) {}
}
